import { getQueryString } from "utils/getQueryString";
import axios from "../axios";

const createCorporate = (data) => axios.post("/corporates", data);

const sanitizeRegistrationNumber = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.replace(/\s+/g, "").toUpperCase().trim();
};

const inviteOrganization = (data) =>
  axios.post("/corporates/invite-organization", data);

const removeOrganization = (corporateId, data) => {
  return axios.put(`/corporates/remove-organization/${corporateId}`, data);
};

const sendInviteNotification = (data) =>
  axios.post("/corporates/send-invite-notification", data);

const submitOrganization = (data) =>
  axios.post(`/corporates/submit-organization`, data);

const getAll = (queryParams) => {
  const queryString = getQueryString(queryParams);
  const url = queryString ? `/corporates?${queryString}` : "/corporates";
  return axios.get(url);
};

const get = (corporateId, options, cancelToken) => {
  const requestConfig = {};
  const params = {};
  let resolvedCancelToken = cancelToken;

  if (options && typeof options === "object" && !Array.isArray(options)) {
    const {
      isDashboard,
      includeDetails,
      params: extraParams,
      cancelToken: optionCancelToken,
    } = options;

    if (isDashboard) {
      params.isDashboard = isDashboard;
    }

    if (typeof includeDetails !== "undefined") {
      params.includeDetails = includeDetails;
    }

    if (extraParams && typeof extraParams === "object") {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (typeof value !== "undefined") {
          params[key] = value;
        }
      });
    }

    if (optionCancelToken) {
      resolvedCancelToken = optionCancelToken;
    }
  } else if (options) {
    params.isDashboard = options;
  }

  if (resolvedCancelToken) {
    requestConfig.cancelToken = resolvedCancelToken;
  }

  if (Object.keys(params).length > 0) {
    requestConfig.params = params;
  }

  return axios.get(`/corporates/${corporateId}`, requestConfig);
};

const update = (corporateId, data) =>
  axios.put(`/corporates/${corporateId}`, data);

const approveOrganization = (data) =>
  axios.put("/corporates/approve-organization", data);

const confirmOrganizationReviewer = (token) =>
  axios.get(`/corporates/confirm-organization-reviewer/${token}`);

const getCorporatesForDashboard = (queryParams) =>
  axios.get(
    `/corporates/get-corporates-for-dashboard?${getQueryString(queryParams)}`
  );

const getTransactions = (corporateId, queryParams) =>
  axios.get(
    `/corporates/${corporateId}/transactions?${getQueryString(queryParams)}`
  );

const lookupByRegistrationNumber = (input, explicitConfig = {}) => {
  let registrationNumber = "";
  let paramsFromInput = {};
  let configFromInput = {};

  if (typeof input === "string") {
    registrationNumber = input;
  } else if (input && typeof input === "object" && !Array.isArray(input)) {
    const {
      registrationNumber: registrationNumberFromInput,
      corporateRegistrationNumber,
      params,
      config,
      cancelToken,
      signal,
      headers,
      timeout,
      auth,
      ...rest
    } = input;

    registrationNumber =
      registrationNumberFromInput || corporateRegistrationNumber || "";

    if (params && typeof params === "object") {
      paramsFromInput = { ...params };
    }

    paramsFromInput = { ...paramsFromInput, ...rest };

    const candidateConfig = {
      ...config,
      ...(cancelToken ? { cancelToken } : {}),
      ...(signal ? { signal } : {}),
      ...(headers ? { headers } : {}),
      ...(timeout ? { timeout } : {}),
      ...(auth ? { auth } : {}),
    };

    if (Object.keys(candidateConfig).length) {
      configFromInput = candidateConfig;
    }
  }

  const sanitizedRegistrationNumber =
    sanitizeRegistrationNumber(registrationNumber);

  const normalizedParams = {
    ...paramsFromInput,
    ...(sanitizedRegistrationNumber
      ? { registrationNumber: sanitizedRegistrationNumber }
      : {}),
  };

  const mergedConfig = {
    ...(configFromInput && typeof configFromInput === "object"
      ? configFromInput
      : {}),
    ...(explicitConfig && typeof explicitConfig === "object"
      ? explicitConfig
      : {}),
  };

  const mergedConfigParams =
    mergedConfig.params && typeof mergedConfig.params === "object"
      ? mergedConfig.params
      : {};

  return axios.get("/corporates/registration-number-lookup", {
    ...mergedConfig,
    params: {
      ...mergedConfigParams,
      ...normalizedParams,
    },
  });
};

const searchByRegistrationNumber = (registrationNumber) =>
  axios.get("/corporates/search-by-registration", {
    params: { registrationNumber },
  });

const findByCorporateRegistrationNumber = (input, explicitConfig = {}) => {
  let registrationNumber = "";
  let paramsFromInput = {};
  let configFromInput = {};

  if (typeof input === "string") {
    registrationNumber = input;
  } else if (input && typeof input === "object" && !Array.isArray(input)) {
    const {
      registrationNumber: registrationNumberFromInput,
      corporateRegistrationNumber,
      params,
      config,
      cancelToken,
      signal,
      headers,
      timeout,
      auth,
      ...rest
    } = input;

    registrationNumber =
      registrationNumberFromInput || corporateRegistrationNumber || "";

    if (params && typeof params === "object") {
      paramsFromInput = { ...params };
    }

    paramsFromInput = { ...paramsFromInput, ...rest };

    const candidateConfig = {
      ...config,
      ...(cancelToken ? { cancelToken } : {}),
      ...(signal ? { signal } : {}),
      ...(headers ? { headers } : {}),
      ...(timeout ? { timeout } : {}),
      ...(auth ? { auth } : {}),
    };

    if (Object.keys(candidateConfig).length) {
      configFromInput = candidateConfig;
    }
  }

  const sanitizedRegistrationNumber =
    sanitizeRegistrationNumber(registrationNumber);

  const normalizedParams = {
    ...paramsFromInput,
    ...(sanitizedRegistrationNumber
      ? { "corporate.registrationNumber": sanitizedRegistrationNumber }
      : {}),
  };

  if (
    sanitizedRegistrationNumber &&
    typeof normalizedParams.limit === "undefined"
  ) {
    normalizedParams.limit = 1;
  }

  const mergedConfig = {
    ...(configFromInput && typeof configFromInput === "object"
      ? configFromInput
      : {}),
    ...(explicitConfig && typeof explicitConfig === "object"
      ? explicitConfig
      : {}),
  };

  const mergedConfigParams =
    mergedConfig.params && typeof mergedConfig.params === "object"
      ? mergedConfig.params
      : {};

  return axios.get("/corporates", {
    ...mergedConfig,
    params: {
      ...mergedConfigParams,
      ...normalizedParams,
    },
  });
};

const corporateDataService = {
  inviteOrganization,
  sendInviteNotification,
  submitOrganization,
  get,
  update,
  approveOrganization,
  confirmOrganizationReviewer,
  getAll,
  getCorporatesForDashboard,
  removeOrganization,
  createCorporate,
  getTransactions,
  lookupByRegistrationNumber,
  searchByRegistrationNumber,
  findByCorporateRegistrationNumber,
};

export default corporateDataService;
